// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

// Create the AuthContext
const AuthContext = createContext();

// Custom hook to use the AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// AuthProvider component
export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [token, setToken] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // API URL from environment or default
  const API_URL = process.env.REACT_APP_API_URL || 'https://api.learninghub.ind.in/api';

  // Initialize auth state from storage on mount
  useEffect(() => {
    const initializeAuth = () => {
      try {
        // Check both sessionStorage and localStorage for persistence
        const sessionToken = sessionStorage.getItem('token');
        const localToken = localStorage.getItem('token');
        const sessionUser = sessionStorage.getItem('userInfo');
        const localUser = localStorage.getItem('userInfo');
        const sessionLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
        const localLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
        const locationData = sessionStorage.getItem('userLocation') || localStorage.getItem('userLocation');

        // Prefer sessionStorage over localStorage
        const storedToken = sessionToken || localToken;
        const storedUser = sessionUser || localUser;
        const loggedIn = sessionLoggedIn || localLoggedIn;

        if (storedToken && storedUser && loggedIn) {
          const parsedUser = JSON.parse(storedUser);
          setToken(storedToken);
          setUserInfo(parsedUser);
          setIsLoggedIn(true);
          
          if (locationData) {
            setUserLocation(JSON.parse(locationData));
          }

          console.log('Auth state initialized from storage:', parsedUser.username);
        } else {
          console.log('No valid auth data found in storage');
        }
      } catch (error) {
        console.error('Error initializing auth state:', error);
        clearAuthData();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (username, password, locationData = null) => {
    try {
      setIsLoading(true);

      // Prepare login data
      const loginData = {
        username,
        password
      };

      // Add location data if available
      if (locationData) {
        loginData.latitude = locationData.lat;
        loginData.longitude = locationData.lng;
        if (locationData.manualLocation) {
          loginData.manualLocation = true;
        }
      }

      // Make API call
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData)
      });

      const result = await response.json();

      if (result.success) {
        // Update state
        setToken(result.token);
        setUserInfo(result.user);
        setIsLoggedIn(true);
        
        if (locationData) {
          setUserLocation(locationData);
        }

        // Store in both session and local storage for persistence
        sessionStorage.setItem('token', result.token);
        sessionStorage.setItem('userInfo', JSON.stringify(result.user));
        sessionStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('token', result.token);
        localStorage.setItem('userInfo', JSON.stringify(result.user));
        localStorage.setItem('isLoggedIn', 'true');
        
        if (locationData) {
          sessionStorage.setItem('userLocation', JSON.stringify(locationData));
          localStorage.setItem('userLocation', JSON.stringify(locationData));
        }

        console.log('Login successful:', result.user.username);
        return { success: true, user: result.user };
      } else {
        console.log('Login failed:', result.message);
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: 'Login failed. Please check your connection and try again.' 
      };
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setIsLoading(true);

      // Call logout API if token exists
      if (token) {
        try {
          await fetch(`${API_URL}/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
        } catch (apiError) {
          console.warn('Logout API call failed:', apiError);
          // Continue with local logout even if API fails
        }
      }

      // Clear all auth data
      clearAuthData();
      console.log('Logout successful');
      
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      // Force clear auth data even on error
      clearAuthData();
      return { success: false, message: 'Logout completed with errors' };
    } finally {
      setIsLoading(false);
    }
  };

  // Clear all authentication data
  const clearAuthData = () => {
    setToken(null);
    setUserInfo(null);
    setIsLoggedIn(false);
    setUserLocation(null);

    // Clear all storage
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('userInfo');
    sessionStorage.removeItem('isLoggedIn');
    sessionStorage.removeItem('userLocation');
    sessionStorage.removeItem('locationGranted');
    sessionStorage.removeItem('userLat');
    sessionStorage.removeItem('userLng');
    sessionStorage.removeItem('locationSkipped');
    sessionStorage.removeItem('manualLocation');
    
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userLocation');
    localStorage.removeItem('currentPage');
  };

  // Update user information
  const updateUserInfo = (updatedInfo) => {
    const newUserInfo = { ...userInfo, ...updatedInfo };
    setUserInfo(newUserInfo);
    
    // Update storage
    sessionStorage.setItem('userInfo', JSON.stringify(newUserInfo));
    localStorage.setItem('userInfo', JSON.stringify(newUserInfo));
  };

  // Update location
  const updateLocation = (locationData) => {
    setUserLocation(locationData);
    sessionStorage.setItem('userLocation', JSON.stringify(locationData));
    localStorage.setItem('userLocation', JSON.stringify(locationData));
  };

  // Check if user has a specific role
  const hasRole = (role) => {
    return userInfo && userInfo.role === role;
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (roles) => {
    return userInfo && roles.includes(userInfo.role);
  };

  // Get role-specific dashboard route
  const getDashboardRoute = () => {
    if (!userInfo || !userInfo.role) return 'home';
    
    const roleDashboards = {
      student: 'student-dashboard',
      teacher: 'teacher-dashboard',
      hod: 'hod-dashboard',
      admin: 'admin-dashboard',
      parent: 'parent-dashboard',
      superadmin: 'superadmin-dashboard'
    };
    
    return roleDashboards[userInfo.role] || 'home';
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (!userInfo) return 'Guest';
    return userInfo.fullName || userInfo.name || userInfo.username || 'User';
  };

  // Refresh user session (useful for keeping session alive)
  const refreshSession = async () => {
    if (!token) return { success: false, message: 'No token available' };

    try {
      const response = await fetch(`${API_URL}/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.user) {
          updateUserInfo(result.user);
          return { success: true };
        }
      }
      
      // If verification fails, clear auth data
      clearAuthData();
      return { success: false, message: 'Session expired' };
    } catch (error) {
      console.error('Session refresh error:', error);
      return { success: false, message: 'Session refresh failed' };
    }
  };

  // Check if user can access a specific route
  const canAccessRoute = (route) => {
    // Public routes
    const publicRoutes = ['home', 'about', 'contact', 'login'];
    if (publicRoutes.includes(route)) return true;

    // Must be logged in for protected routes
    if (!isLoggedIn || !userInfo) return false;

    // Role-specific route checks
    const roleRoutes = {
      student: [
        'student-dashboard', 'student-profile', 'student-fees', 
        'student-internship', 'student-cards', 'student-forms', 
        'student-marks', 'student-marks-review', 'student-grievance'
      ],
      teacher: ['teacher-dashboard'],
      hod: ['hod-dashboard'],
      admin: ['admin-dashboard'],
      parent: ['parent-dashboard'],
      superadmin: ['superadmin-dashboard']
    };

    const userRoutes = roleRoutes[userInfo.role] || [];
    return userRoutes.includes(route);
  };

  const value = {
    // State
    isLoggedIn,
    userInfo,
    token,
    userLocation,
    isLoading,

    // Actions
    login,
    logout,
    clearAuthData,
    updateUserInfo,
    updateLocation,
    refreshSession,

    // Utility functions
    hasRole,
    hasAnyRole,
    getDashboardRoute,
    getUserDisplayName,
    canAccessRoute,

    // Constants
    API_URL
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;